// src/shared/email-recipients.js — recipient rules shared by BOTH sides.
//
// v129.19. Zero dependencies, deliberately importable by the browser bundle as
// well as the Worker (same precedent as BADGES in src/worker/achievements.js).
//
// Why shared rather than copied: the 20-recipient ceiling is a SEND-PATH rule.
// The compose picker has to know the same number so a department group refuses
// to overflow the To field at tap time, instead of the person writing the whole
// email and only then being told "Too many recipients" by the send route. Two
// copies of that number would drift the moment one is raised — exactly what
// happened when the cap went 5 → 20 for Mercato Mall in v126.67.
//
// src/worker/store-email.js re-exports these so its existing callers are
// unchanged and there is still only ONE definition of each rule.

// A single Resend message carries all recipients in `to:` (Resend allows ~50),
// so this is a product choice, not a provider limit — generous for a store
// emailing HO + area + a few suppliers. Raised 5 → 20 in v126.67.
//
// NOTE what this rules out on purpose: a department of 28 stores cannot be a
// group. That is a broadcast, and broadcast belongs in News, not in every
// store's mailbox.
export const MAX_RECIPIENTS = 20;

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Wafi City <Wafi@ALGT-me.com>" -> "wafi@algt-me.com". Lowercased, trimmed.
export function normaliseAddress(s) {
  if (s === null || s === undefined) return '';
  const str = String(s);
  const m = str.match(/<([^>]+)>/);
  return (m ? m[1] : str).trim().toLowerCase();
}

// Active accounts with a real email + a department -> one entry per department.
//
// The group is DERIVED from each person's department, so adding someone to
// Finance puts them in the Finance group with nothing to maintain — that is the
// whole reason this is a field on the person rather than a separate list of
// groups (which would rot the moment somebody joined or left).
//
// Case/whitespace fold so 'finance', 'Finance ' and 'FINANCE' are one group;
// the first spelling seen supplies the label.
export function groupAccountsByDepartment(accounts, opts) {
  const skip = (opts && opts.skipAddresses) || {};
  const byKey = new Map();
  for (const a of Array.isArray(accounts) ? accounts : []) {
    if (!a || a.active === false) continue;
    const dept = String(a.department == null ? '' : a.department).trim();
    if (!dept) continue;
    const addr = normaliseAddress(a.email);
    if (!addr || !EMAIL_RE.test(addr) || skip[addr]) continue;
    const key = dept.toLowerCase();
    if (!byKey.has(key)) byKey.set(key, { name: dept, members: [] });
    const g = byKey.get(key);
    if (!g.members.some((m) => m.address === addr)) {
      g.members.push({ name: String(a.name || ''), address: addr });
    }
  }
  return [...byKey.values()]
    .map((g) => ({
      name: g.name,
      members: g.members.slice().sort((x, y) => x.address.localeCompare(y.address)),
    }))
    .filter((g) => g.members.length)
    .sort((x, y) => x.name.localeCompare(y.name));
}

// What tapping a group should do to the To field. Returns { ok:true, to, added }
// with the merged list, or { ok:false, reason, would } when it would break
// MAX_RECIPIENTS. Addresses already in the field are not counted twice, so
// tapping the same group again is a no-op rather than an overflow.
export function addGroupToRecipients(currentTo, groupAddresses) {
  const have = [];
  for (const raw of String(currentTo || '').split(/[,;]/)) {
    const a = normaliseAddress(raw);
    if (a && have.indexOf(a) === -1) have.push(a);
  }
  const added = [];
  for (const raw of Array.isArray(groupAddresses) ? groupAddresses : []) {
    const a = normaliseAddress(raw);
    if (a && EMAIL_RE.test(a) && have.indexOf(a) === -1 && added.indexOf(a) === -1) added.push(a);
  }
  const total = have.length + added.length;
  if (total > MAX_RECIPIENTS) {
    return {
      ok: false,
      would: total,
      reason: 'That would be ' + total + ' recipients — the limit is ' + MAX_RECIPIENTS + '.',
    };
  }
  return { ok: true, to: have.concat(added).join(', '), added: added.length };
}

// ── v129.144 — REPLY ALL (Mercato Mall, 14 Aug) ──────────────────────────────
// "All recipients included in the original email should also be included when
// replying… There is no 'Reply All' option."
//
// Reply used only `from_addr`, so a store answering a thread addressed to
// several people answered ONE of them and had to re-type the rest by hand.
//
// PURE + shared so the Worker and the browser agree on one definition.
//
// The rules, in order:
//   1. reply_to beats from — a sender who set Reply-To meant it.
//   2. Then every original To: and Cc:, in that order, de-duplicated.
//   3. OUR OWN addresses are removed. The letterbox domain is stripped by
//      suffix (every store's inbound address lives there), plus any address
//      the caller knows is ours. Without this, Reply all mails the store
//      itself and the thread grows a copy of every message.
//   4. Anything that is not a valid address is dropped rather than sent.
export const LETTERBOX_SUFFIX = '@in.elctoysfeedback.com';

export function replyAllRecipients(email, selfAddrs) {
  const m = email || {};
  const mine = {};
  (Array.isArray(selfAddrs) ? selfAddrs : []).forEach((a) => {
    const n = normaliseAddress(a);
    if (n) mine[n] = true;
  });
  const out = [];
  const push = (raw) => {
    const a = normaliseAddress(raw);
    if (!a || !EMAIL_RE.test(a)) return;
    if (mine[a]) return;
    if (a.slice(-LETTERBOX_SUFFIX.length) === LETTERBOX_SUFFIX) return;
    if (out.indexOf(a) === -1) out.push(a);
  };
  const primary = (Array.isArray(m.reply_to) && m.reply_to.length) ? m.reply_to : [m.from_addr];
  primary.forEach(push);
  (Array.isArray(m.to) ? m.to : []).forEach(push);
  (Array.isArray(m.cc) ? m.cc : []).forEach(push);
  return out.slice(0, MAX_RECIPIENTS);
}

// Is there anyone BEYOND the person we would reply to anyway? Drives whether
// the "Reply all" button is worth showing — offering it when it would do
// exactly what Reply does is just another button to think about.
export function hasMoreThanSender(email, selfAddrs) {
  return replyAllRecipients(email, selfAddrs).length > 1;
}
